import { Grid, Box, Divider } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import {
  Body1,
  Caption,
  H1,
  PrimaryButton,
  SecondaryButton,
  startNextCase,
  authorizeBaseUrl,
  useParsedQueryString,
  useSyncQueryParamsToPath,
  convertHealthPlanEnumToName,
} from "@coherehealth/common";
import DashboardMetrics from "components/DashboardPage/DashboardMetrics";
import { useEffect, useState, useContext, useCallback, useMemo } from "react";
import {
  useGetNextCaseForQueue,
  useGetServiceCase,
  useGetServiceRequestNonClosedCasesByType,
} from "@coherehealth/qm-api";
import config from "api/config";
import { useSnackbar } from "notistack";
import LoadingDialog from "components/common/modals/LoadingDialog";
import { UserContext } from "UserContext";
import routes from "routes";
import { useGetLettersExpansionConfigurationByPayer } from "hooks/useGetFeatureConfigurations";
import { useLaunchDarklyFeatureFlag } from "hooks/useLaunchDarklyFeatureFlag";
import { ContinueToClinicalReviewBanner } from "./PageSections/ContinueToClinicalReviewBanner";
import { useClinicalReviewStyles } from "./caseCompletePageStyles";
import {
  auditPillDisplay,
  getCaseCompleteHeader,
  getServiceCaseCohereIds,
  parseSplitFaxQueryParams,
  reviewPillDisplay,
} from "./caseCompletePageUtils";
import { PillSection } from "./PageSections/PillSection";
import { OutcomeSection } from "./PageSections/OutcomeSection";
import { SplitFaxOutcomeSection } from "./PageSections/SplitFaxOutcomeSection";

const CaseCompletePage = () => {
  const classes = useClinicalReviewStyles();
  const { caseId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [cohereIds, setCohereIds] = useState<Array<string>>([]);

  const { activeQueue, queueView } = useContext(UserContext);
  const qs = useParsedQueryString();
  const patientId = qs["patientId"];
  const reviewServiceRequestId = qs["reviewServiceRequestId"];
  const origin = qs["origin"];
  const followUpReview = qs["followUpReview"];
  useSyncQueryParamsToPath({ ...qs, queueId: activeQueue?.id });

  const splitFaxData = useMemo(() => parseSplitFaxQueryParams(qs), [qs]);

  const {
    data: serviceCase,
    loading: serviceCaseLoading,
    error: getServiceCaseError,
  } = useGetServiceCase({ id: caseId ? caseId : "" });

  const { data: lettersExpansionConfig } = useGetLettersExpansionConfigurationByPayer(
    convertHealthPlanEnumToName(serviceCase?.healthPlan || "")
  );

  const { featureFlagEnabled: adhocLettersEnabled } = useLaunchDarklyFeatureFlag({
    featureFlagName: "adhocLettersEnabled",
    context: {
      healthPlanName: convertHealthPlanEnumToName(serviceCase?.healthPlan || ""),
      delegatedVendorName: serviceCase?.delegatedVendor ?? "",
    },
  });

  const adhocLetterPath = adhocLettersEnabled ? "/adhoc_letter/" : "/letter/";

  const {
    loading: nextServiceCaseLoadingFromQueue,
    error: getNextServiceCaseErrorFromQueue,
    refetch: getNextServiceCaseFromQueue,
  } = useGetNextCaseForQueue({
    queueId: "",
    lazy: true,
  });

  const fetchCase = useCallback(async () => {
    const serviceCaseFromQueue = await getNextServiceCaseFromQueue({
      pathParams: { queueId: activeQueue?.id },
      queryParams: { timeStamp: new Date().toISOString() },
    });

    if (serviceCaseFromQueue) {
      startNextCase(
        () => {},
        serviceCaseFromQueue,
        enqueueSnackbar,
        true,
        false,
        activeQueue?.id,
        activeQueue?.routeConfigByCaseType
      );
    }
  }, [activeQueue?.id, enqueueSnackbar, getNextServiceCaseFromQueue, activeQueue?.routeConfigByCaseType]);

  const continuationSrId = serviceCase?.outcome?.continuationId;

  const { data: continuationCases, refetch: fetchContinuationCases } = useGetServiceRequestNonClosedCasesByType({
    id: continuationSrId || "",
    base: `${config.QM_SERVICE_API_URL}`,
    lazy: true,
    queryParams: { caseTypes: "RN_REVIEW" },
  });

  useEffect(() => {
    if (continuationSrId) {
      fetchContinuationCases();
    }
  }, [continuationSrId, fetchContinuationCases]);

  const startConcurrentReview = useCallback(() => {
    const rnReviewCaseId = continuationCases?.at(0)?.id;
    const caseIdParam = rnReviewCaseId ? `&caseId=${rnReviewCaseId}` : "";
    const followUpParam = caseId ? `&isConcurrentReview=true&followUpCaseId=${caseId}` : "";
    window.location.assign(
      `${authorizeBaseUrl()}/${continuationSrId}/review?createNewNurseReview${caseIdParam}&queueId=${
        activeQueue?.id
      }&origin=${origin}${followUpParam}`
    );
  }, [continuationSrId, continuationCases, activeQueue?.id, origin, caseId]);

  const isClinicalReviewCase = ![
    "FAX_INTAKE",
    "LETTER_WRITING",
    "MISSING_INFORMATION",
    "NEW_HIRE_AUDIT_REVIEW",
    "FAX_AUDIT_REVIEW",
    "FOLLOW_UP",
  ].includes(serviceCase?.caseType || "");
  const isP2PReviewCase = serviceCase?.caseType === "P2P_REVIEW";
  const isOutreachCase = serviceCase?.caseType === "MISSING_INFORMATION";
  const isFaxIntakeCase = serviceCase?.caseType === "FAX_INTAKE";
  const isFaxAuditReviewCase = serviceCase?.caseType === "FAX_AUDIT_REVIEW";

  const isFollowUpCaseCompleted = serviceCase?.caseType === "FOLLOW_UP" && followUpReview === "Completed";
  const canContinueToClinicalReview =
    serviceCase?.healthPlan === "GEISINGER" &&
    isFaxIntakeCase &&
    (serviceCase.outcome?.outcome === "NEW_SR" ||
      (serviceCase.outcome?.outcome === "FAX_ATTACHED" &&
        serviceCase?.outcome?.serviceRequests?.[0]?.status !== "APPROVED"));

  useEffect(() => {
    const cohereIds = getServiceCaseCohereIds(serviceCase);

    setCohereIds(cohereIds || []);
  }, [serviceCase]);

  useEffect(() => {
    if (getServiceCaseError) {
      enqueueSnackbar("Error fetching case", {
        variant: "error",
        preventDuplicate: true,
      });
    }
    if (getNextServiceCaseErrorFromQueue) {
      enqueueSnackbar("Error fetching next case", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  }, [getServiceCaseError, getNextServiceCaseErrorFromQueue, enqueueSnackbar]);

  if (splitFaxData) {
    return (
      <Grid container direction="column" justifyContent="flex-start" alignItems="center" height="100vh">
        <LoadingDialog open={nextServiceCaseLoadingFromQueue} headingText="Loading" />
        <Grid container item xs={8} direction="column" justifyContent="center" alignItems="center">
          <Grid item>
            <H1 className={classes.requestSubmittedGridPadding}>Split fax successful!</H1>
          </Grid>
          <Grid container direction="row" justifyContent="center" alignItems="center" style={{ padding: "32px" }}>
            <SplitFaxOutcomeSection splitFaxData={splitFaxData} />
          </Grid>
          <Box>
            <SecondaryButton className={classes.secondaryButton} to={routes.DASHBOARD} component={Link}>
              Back to My Work
            </SecondaryButton>
            <PrimaryButton className={classes.primaryButton} onClick={fetchCase}>
              Start next case
            </PrimaryButton>
          </Box>
        </Grid>
        <Grid container item xs={2} direction="column" justifyContent="space-between" alignItems="center">
          <Divider className={classes.divider} variant="fullWidth" />
          <Grid justifyContent="center" alignItems="center">
            <DashboardMetrics queueView={queueView} activeQueueId={activeQueue?.id} />
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return serviceCase ? (
    <Grid container direction="column" justifyContent="flex-start" alignItems="center" height="100vh">
      <LoadingDialog open={serviceCaseLoading || nextServiceCaseLoadingFromQueue} headingText="Loading" />
      <Grid container item xs={8} direction="column" justifyContent="center" alignItems="center">
        <Grid item>
          <H1 className={classes.requestSubmittedGridPadding}>
            {getCaseCompleteHeader(
              serviceCase?.outcome?.outcome,
              isClinicalReviewCase,
              serviceCase?.caseType,
              isFollowUpCaseCompleted
            )}
          </H1>
        </Grid>
        {canContinueToClinicalReview && (
          <Grid container direction="row" justifyContent="center" alignItems="center">
            <ContinueToClinicalReviewBanner />
          </Grid>
        )}
        <Grid container direction="row" justifyContent="center" alignItems="center" style={{ padding: "32px" }}>
          {serviceCase?.caseType !== "FAX_INTAKE" ? (
            <>
              {(isClinicalReviewCase || isOutreachCase || isFaxAuditReviewCase) && (
                <PillSection pillDataDisplay={reviewPillDisplay(serviceCase)} />
              )}
              {(serviceCase?.caseType === "NEW_HIRE_AUDIT_REVIEW" || isFaxAuditReviewCase) && (
                <PillSection pillDataDisplay={auditPillDisplay(serviceCase)} />
              )}
              {isFaxAuditReviewCase && (
                <Box className={classes.outcomeCaseTrackingBox}>
                  <Caption className={classes.caption}>Case ID</Caption>
                  <Body1 className={classes.body1}>{serviceCase?.caseNumber}</Body1>
                </Box>
              )}
              <Box className={classes.outcomeCaseTrackingBox}>
                <Caption className={classes.caption}>Tracking#</Caption>
                <Body1 className={classes.body1}>{serviceCase?.serviceRequest?.cohereId}</Body1>
              </Box>
            </>
          ) : (
            <OutcomeSection serviceCase={serviceCase} cohereIds={cohereIds} />
          )}
        </Grid>

        {isP2PReviewCase ? (
          <Box>
            <SecondaryButton
              className={classes.secondaryButton}
              onClick={() => {
                window.location.assign(
                  `${authorizeBaseUrl()}/patient_summary/${patientId}?reviewServiceRequestId=${reviewServiceRequestId}`
                );
              }}
            >
              Back to patient summary
            </SecondaryButton>
            <PrimaryButton className={classes.primaryButton} to={routes.DASHBOARD} component={Link}>
              Back to My Work
            </PrimaryButton>
          </Box>
        ) : (
          <Box>
            <SecondaryButton className={classes.secondaryButton} to={routes.DASHBOARD} component={Link}>
              Back to My Work
            </SecondaryButton>
            {lettersExpansionConfig?.adhocLetter &&
              (serviceCase?.caseType === "RN_REVIEW" || isFollowUpCaseCompleted) && (
                <SecondaryButton
                  className={classes.secondaryButton}
                  onClick={() => {
                    window.open(
                      `${authorizeBaseUrl()}${adhocLetterPath}${serviceCase?.serviceRequest?.serviceRequestId}?caseId=${
                        serviceCase?.id
                      }`
                    );
                  }}
                >
                  Send a letter
                </SecondaryButton>
              )}
            {serviceCase?.caseType === "FOLLOW_UP" && serviceCase?.outcome?.continuationInitiated && !followUpReview ? (
              <PrimaryButton className={classes.primaryButtonFollowUp} onClick={startConcurrentReview}>
                Start concurrent review
              </PrimaryButton>
            ) : (
              <PrimaryButton className={classes.primaryButton} onClick={fetchCase}>
                Start next case
              </PrimaryButton>
            )}
          </Box>
        )}
      </Grid>
      <Grid container item xs={2} direction="column" justifyContent="space-between" alignItems="center">
        {!isP2PReviewCase && (
          <>
            <Divider className={classes.divider} variant="fullWidth" />
            <Grid justifyContent="center" alignItems="center">
              <DashboardMetrics queueView={queueView} activeQueueId={activeQueue?.id} />
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  ) : null;
};

export default CaseCompletePage;
